/**
 * InfluencerSpotlight — single-card spotlight on the most-followed passenger
 * social-media voice verified in the past 24 h.
 *
 * Design rules (uncodixify-ui.md):
 *   - rounded-lg, shadow-sm, no hover lift, no shadow escalation.
 *   - Platform pill uses semantic colour tokens + explicit dark variants.
 *   - No animate-pulse anywhere.
 *   - External-link icon mirrors in RTL via rtl:scale-x-[-1].
 *   - Touch targets ≥ 44 × 44 px via parent container.
 *   - Avatar: text initial only — no remote image fetch (privacy-safe).
 *
 * RTL rules (rtl-support-arabic.md):
 *   - Logical CSS: ms-*, me-*, text-start, text-end.
 *   - No rtl:flex-row-reverse.
 *
 * Accessibility:
 *   - "View original post" link has descriptive aria-label.
 *   - Platform pill has role="status" for screen readers.
 *
 * Prompt #11 (passenger-silence-and-influencer-feed).
 */

import { ExternalLink } from "lucide-react";

import { useTopInfluencer } from "#/hooks/usePassengerVoices.js";
import type { TranslateFn } from "#/lib/i18n.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InfluencerSpotlightProps {
  slug: string;
  locale: string;
  t: TranslateFn;
}

type Platform = "instagram" | "tiktok" | "x" | "facebook" | "threads";

// ---------------------------------------------------------------------------
// Platform pill colours
// ---------------------------------------------------------------------------

const PLATFORM_COLOURS: Record<Platform, string> = {
  instagram:
    "bg-pink-50 text-pink-900 border border-pink-200 dark:bg-pink-900/20 dark:text-pink-300 dark:border-pink-800",
  tiktok:
    "bg-slate-50 text-slate-900 border border-slate-200 dark:bg-slate-800/30 dark:text-slate-300 dark:border-slate-700",
  x:
    "bg-sky-50 text-sky-900 border border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800",
  facebook:
    "bg-blue-50 text-blue-900 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800",
  threads:
    "bg-zinc-50 text-zinc-900 border border-zinc-200 dark:bg-zinc-800/30 dark:text-zinc-300 dark:border-zinc-700",
};

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X (Twitter)",
  facebook: "Facebook",
  threads: "Threads",
};

function PlatformPill({ platform }: { platform: Platform }) {
  return (
    <span
      role="status"
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${PLATFORM_COLOURS[platform]}`}
    >
      {PLATFORM_LABELS[platform]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Text avatar (initial only — no remote image fetch)
// ---------------------------------------------------------------------------

function TextAvatar({ handle }: { handle: string }) {
  const initial = handle.replace(/^@/, "")[0]?.toUpperCase() ?? "?";
  return (
    <div
      aria-hidden="true"
      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-muted text-base font-semibold text-muted-foreground select-none" /* uncodixify-ok: avatar initial circle */
    >
      {initial}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Follower count formatter
// ---------------------------------------------------------------------------

function fmtFollowers(n: number | null, locale: string): string | null {
  if (n === null) return null;
  try {
    // Use compact notation: 320k, 1.2M
    return new Intl.NumberFormat(locale === "ar" ? "ar-EG" : locale, {
      notation: "compact",
      maximumSignificantDigits: 3,
    }).format(n);
  } catch {
    return String(n);
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function InfluencerSpotlight({
  slug,
  locale,
  t,
}: InfluencerSpotlightProps) {
  const { data, isLoading } = useTopInfluencer({ slug });

  if (isLoading) {
    return (
      <aside
        aria-label={t("passengerSilence:spotlight.ariaLabel")}
        className="rounded-lg border border-border bg-card shadow-sm p-6"
      >
        <p className="text-sm text-muted-foreground" aria-live="polite" aria-busy="true">
          {t("passengerSilence:spotlight.loading")}
        </p>
      </aside>
    );
  }

  return (
    <aside
      aria-labelledby="influencer-spotlight-heading"
      className="rounded-lg border border-border bg-card shadow-sm p-6 space-y-4"
    >
      <h2
        id="influencer-spotlight-heading"
        className="text-base font-semibold text-foreground"
      >
        {t("passengerSilence:spotlight.heading")}
      </h2>

      {/* Empty state: no verified voice in past 24 h */}
      {!data && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t("passengerSilence:empty")}
        </p>
      )}

      {/* Influencer card */}
      {data && (
        <div className="space-y-3">
          {/* Handle + avatar row */}
          <div className="flex items-center gap-3">
            <TextAvatar handle={data.handle} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">
                {data.handle.startsWith("@") ? data.handle : `@${data.handle}`}
              </p>
              {data.followersCount !== null && (
                <p className="text-xs text-muted-foreground">
                  {t("passengerSilence:spotlight.followers", {
                    count: fmtFollowers(data.followersCount, locale) ?? "",
                  })}
                </p>
              )}
            </div>
            <PlatformPill platform={data.platform} />
          </div>

          {/* Content excerpt */}
          <blockquote className="rounded-md border-s-2 border-border ps-3 text-sm text-muted-foreground italic">
            {data.contentExcerpt}
          </blockquote>

          {/* View original post — touch target ≥ 44 px via min-h-[44px] on the row */}
          <div className="flex items-center min-h-[44px]">
            <a
              href={data.postUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-2 hover:underline transition-colors duration-150"
              aria-label={`${t("passengerSilence:spotlight.viewPost")} — ${data.handle}`}
            >
              {t("passengerSilence:spotlight.viewPost")}
              <ExternalLink
                className="h-3.5 w-3.5 flex-shrink-0 rtl:scale-x-[-1]"
                aria-hidden="true"
              />
            </a>
          </div>

          {/* Source provenance */}
          <p className="text-xs text-muted-foreground text-start">
            {t("passengerSilence:spotlight.source")}{" "}
            <a
              href={data.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-foreground transition-colors duration-150"
              aria-label={`${t("passengerSilence:spotlight.source")} ${data.sourceUrl}`}
            >
              {t("passengerSilence:spotlight.sourceLink")}
            </a>
          </p>
        </div>
      )}
    </aside>
  );
}
